// src/features/product/ProductCreateDialog.tsx
// Create a product — form-kit dialog (docs/ux/14-forms.md), opened from
// ProductListPage's "+ New" instead of routing to a /products/new page.
// Redirect URIs and scopes are list editors (ChipInput) instead of raw
// comma-in-a-text-box fields — CreateProductRequest carries both as
// string[] already, the generated form just flattened them.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useProduct } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { ChipInput } from './ChipInput';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export interface ProductCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string | undefined) => void;
}

export function ProductCreateDialog({ open, onClose, onCreated }: ProductCreateDialogProps) {
  const { t } = useI18n();
  const store = useProduct();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagline, setTagline] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [allowedScopes, setAllowedScopes] = useState<string[]>([]);
  const [moreFields, setMoreFields] = useState(false);
  const [iconUrl, setIconUrl] = useState('');
  const [webUrl, setWebUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setTagline('');
    setSlug('');
    setSlugTouched(false);
    setRedirectUris([]);
    setAllowedScopes([]);
    setMoreFields(false);
    setIconUrl('');
    setWebUrl('');
    setError(null);
  }, [open]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const dirty = !!(name.trim() || description.trim());

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await store.CreateProduct({
        name: name.trim(),
        slug: effectiveSlug || slugify(name.trim()),
        description: description.trim(),
        tagline: tagline.trim(),
        redirect_uris: redirectUris,
        allowed_scopes: allowedScopes,
        icon_url: iconUrl.trim(),
        web_url: webUrl.trim(),
      } as never);
      const newId = (result as any)?.entity?.id || (result as any)?.id;
      onCreated(newId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('product.create')}
      canSubmit={!!name.trim()}
      isSubmitting={submitting}
      dirty={dirty}
      width="lg"
      aria-label={t('product.create')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('common.fewerFields') : t('common.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('product.namePlaceholder')}
          initialFocus
          aria-label={t('product.field.name')}
        />
        <AutoGrowTextarea
          value={description}
          onChange={setDescription}
          placeholder={t('product.descriptionPlaceholder')}
          minRows={1}
        />
      </div>

      <div className="px-3.5 pb-3">
        <input
          type="text"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder={t('product.field.taglinePlaceholder')}
          aria-label={t('product.field.tagline')}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <FormRow label={t('product.field.slug')} labelWidth="sm">
            <input
              type="text"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              }}
              placeholder="my-product"
              className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('product.field.iconUrl')} labelWidth="sm">
            <input
              type="url"
              value={iconUrl}
              onChange={(e) => setIconUrl(e.target.value)}
              placeholder="https://…"
              className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('product.field.webUrl')} labelWidth="sm">
            <input
              type="url"
              value={webUrl}
              onChange={(e) => setWebUrl(e.target.value)}
              placeholder="https://…"
              className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('product.field.redirectUris')} labelWidth="sm" hint={t('product.field.redirectUrisHint')}>
            <ChipInput value={redirectUris} onChange={setRedirectUris} placeholder="https://app.example.com/callback" aria-label={t('product.field.redirectUris')} />
          </FormRow>
          <FormRow label={t('product.field.allowedScopes')} labelWidth="sm">
            <ChipInput value={allowedScopes} onChange={setAllowedScopes} placeholder="read:tasks" aria-label={t('product.field.allowedScopes')} />
          </FormRow>
        </div>
      )}
    </FormDialog>
  );
}

export default ProductCreateDialog;
